import Stack from '@mui/material/Stack';
import {
  DataGrid,
  type GridColDef,
  type GridFilterModel,
  type GridRowParams,
  type GridSingleSelectColDef,
  type GridSortModel,
  type ValueOptions,
} from '@mui/x-data-grid';
import {enUS, esES} from '@mui/x-data-grid/locales';
import type {TFunction} from 'i18next';
import {type FC, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import StatusInfo from '@/components/common/StatusInfo';
import {statusValueOptions} from '@/components/ForestMassList/statusValueOptions';
import {type ForestMassStatus, STATUS_ASEFOR, STATUS_FINSA, STATUS_FORTOP} from '@/domain/entities/ForestMassStatus';
import {createStatusSortComparator, type Organization} from './statusSortComparator';

type Props = {
  forestMassesStatuses?: Array<ForestMassStatus>;
  onForestMassClick: (uuid: string) => void;
  sortModel?: GridSortModel;
  onSortModelChange?: (model: GridSortModel) => void;
  filterModel?: GridFilterModel;
  onFilterModelChange?: (model: GridFilterModel) => void;
};

const ForestMassesTable: FC<Props> = ({
  forestMassesStatuses,
  onForestMassClick,
  sortModel,
  onSortModelChange,
  filterModel,
  onFilterModelChange,
}) => {
  const {t, i18n} = useTranslation();
  const lang = i18n.resolvedLanguage ?? i18n.language;

  const baseLocale = lang.startsWith('es')
    ? esES.components.MuiDataGrid.defaultProps.localeText
    : enUS.components.MuiDataGrid.defaultProps.localeText;

  // Statuses present in the current data, separated by organization
  const statusesPresent = useMemo(() => {
    const masses = forestMassesStatuses ?? [];

    return {
      finsa: new Set<STATUS_FINSA>(
        masses.map((mass) => mass.finsaStatus).filter((status): status is STATUS_FINSA => !!status),
      ),
      asefor: new Set<STATUS_ASEFOR>(
        masses.map((mass) => mass.aseforStatus).filter((status): status is STATUS_ASEFOR => !!status),
      ),
      fortop: new Set<STATUS_FORTOP>(
        masses.map((mass) => mass.fortopStatus).filter((status): status is STATUS_FORTOP => !!status),
      ),
    };
  }, [forestMassesStatuses]);

  // Columns
  const columns = useMemo<GridColDef[]>(
    () => [
      createColumn('createdAt', t('forestMass.date').toUpperCase(), {flex: 0.75}),
      createColumn('code', t('forestMass.code').toUpperCase(), {flex: 0.5}),
      createColumn('concello', t('forestMass.concello').toUpperCase(), {flex: 0.5}),
      createColumn('appraiserName', t('forestMass.appraiser').toUpperCase(), {flex: 0.75}),
      createColumn('plannedCuttingDate', t('forestMass.plannedCuttingDate').toUpperCase()),
      createStatusColumn(
        'finsa',
        'FINSA',
        statusValueOptions(Object.values(STATUS_FINSA), statusesPresent.finsa, t),
        t,
        lang,
      ),
      createStatusColumn(
        'asefor',
        'ASEFOR',
        statusValueOptions(Object.values(STATUS_ASEFOR), statusesPresent.asefor, t),
        t,
        lang,
      ),
      createStatusColumn(
        'fortop',
        'FORTOP',
        statusValueOptions(Object.values(STATUS_FORTOP), statusesPresent.fortop, t),
        t,
        lang,
      ),
    ],
    [t, lang, statusesPresent],
  );

  // Rows
  const forestMassesForTable = useMemo(
    () =>
      forestMassesStatuses?.map((mass) => ({
        id: mass.forestMassUuid,
        code: mass.code,
        createdAt: mass.createdAt.format('YYYY-MM-DD'),
        concello: mass.concello,
        appraiserName: mass.appraiserName,
        plannedCuttingDate: mass.plannedCuttingDate.format('YYYY-MM-DD'),
        finsaStatus: mass.finsaStatus,
        finsaStatusChangeDate: mass.finsaStatusChangeDate,
        aseforStatus: mass.aseforStatus,
        aseforStatusChangeDate: mass.aseforStatusChangeDate,
        fortopStatus: mass.fortopStatus,
        fortopStatusChangeDate: mass.fortopStatusChangeDate,
      })),
    [forestMassesStatuses],
  );

  const handleForestMassClick = (params: GridRowParams) => {
    onForestMassClick(params.id as string);
  };

  return (
    <DataGrid
      rows={forestMassesForTable ?? []}
      columns={columns}
      disableColumnSelector
      filterModel={filterModel}
      onFilterModelChange={onFilterModelChange}
      getRowHeight={() => 'auto'}
      onRowClick={handleForestMassClick}
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      localeText={{
        ...baseLocale,
        noRowsLabel: t('forestMass.empty'),
      }}
      sx={styles.table}
    />
  );
};

export default ForestMassesTable;

const createColumn = (field: string, label: string, extraProps?: Partial<GridColDef>): GridColDef => ({
  field,
  headerName: label,
  flex: 1,
  ...extraProps,
});

const createStatusColumn = (
  org: Organization,
  label: string,
  valueOptions: ValueOptions[],
  t: TFunction,
  lang: string,
): GridSingleSelectColDef => ({
  field: `${org}Status`,
  headerName: label,
  flex: 1,
  type: 'singleSelect',
  valueOptions,
  getOptionLabel: (option) => (isOptionObject(option) ? String(option.label) : String(option)),
  getOptionValue: (option) => (isOptionObject(option) ? option.value : option),
  sortComparator: createStatusSortComparator(org, t, lang),
  renderCell: (params) => (
    <Stack my={2}>
      <StatusInfo id={params.row[`${org}Status`]} date={params.row[`${org}StatusChangeDate`]} />
    </Stack>
  ),
});

const isOptionObject = (option: ValueOptions): option is {value: unknown; label: string} =>
  typeof option === 'object' && option !== null && 'value' in option && 'label' in option;

const styles = {
  table: {
    width: '100%',
    boxShadow: 'none',
    borderRadius: '0px',
    '& .MuiDataGrid-cell': {
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiPaper-root': {
      boxShadow: 'none !important',
    },
    '--Paper-shadow': 'none',
    '& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeaderTitle': {
      color: 'common.white',
      fontWeight: 900,
      backgroundColor: 'primary.main',
    },
    '& .MuiDataGrid-cell:focus': {outline: 'none'},
    '& .MuiDataGrid-columnHeader--sortable .MuiSvgIcon-root': {
      color: 'common.white',
    },
    '& .MuiDataGrid-columnHeader:hover .MuiIconButton-root': {
      backgroundColor: 'primary.main',
      color: 'common.white',
    },
    '& .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIcon': {
      visibility: 'visible',
      width: 'auto',
    },
    '& .MuiDataGrid-columnHeader--sorted .MuiButtonBase-root': {
      backgroundColor: 'primary.main',
    },
  },
};
